/**
 * OMSchedulePage.tsx — Operations & Maintenance schedule generator
 *
 * Generates a year-by-year O&M calendar covering:
 *   - Daily / weekly / monthly / quarterly / annual / 5-yearly tasks
 *   - Spares register
 *   - Recovery-cleaning cycle aligned to fouling-drift curve
 *   - Blower service intervals
 *   - SCADA alarm response
 *
 * Standard appendix in any aeration scheme handover pack.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Wrench, ArrowLeft, Plus, Trash2, Save, Printer } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { toast } from 'sonner';

const KEY = 'rb_om_schedule_v1';

interface Task {
  id: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual' | '5-yearly' | 'event-driven';
  category: 'aeration' | 'blower' | 'controls' | 'sensors' | 'civils' | 'compliance';
  task: string;
  responsibility: string;
  notes: string;
}

interface Spare {
  id: string;
  item: string;
  qtyOnSite: number;
  qtyOnOrder: number;
  leadTimeWeeks: number;
  supplier: string;
  partNumber: string;
}

interface OMState {
  tasks: Task[];
  spares: Spare[];
  contractStart: string;
  reviewInterval: number;
}

function uid() { return 'i' + Math.random().toString(36).slice(2, 8); }

const DEFAULT_TASKS: Task[] = [
  { id: 't1', frequency: 'daily',     category: 'controls',  task: 'Review SCADA DO trend and any alarms', responsibility: 'Operator', notes: '' },
  { id: 't2', frequency: 'daily',     category: 'blower',    task: 'Check blower running hours and oil temperature', responsibility: 'Operator', notes: '' },
  { id: 't3', frequency: 'weekly',    category: 'aeration',  task: 'Visual surface boil pattern check from embankment', responsibility: 'Operator', notes: 'Photo log' },
  { id: 't4', frequency: 'weekly',    category: 'sensors',   task: 'DO probe drift check against handheld', responsibility: 'Operator', notes: 'BS EN ISO 5814' },
  { id: 't5', frequency: 'monthly',   category: 'blower',    task: 'Inlet filter inspection / replacement', responsibility: 'Maintenance', notes: '' },
  { id: 't6', frequency: 'monthly',   category: 'controls',  task: 'PLC log review, fault history backup', responsibility: 'ICA tech', notes: '' },
  { id: 't7', frequency: 'monthly',   category: 'sensors',   task: 'CTD profile at 4 stations', responsibility: 'Water quality', notes: '' },
  { id: 't8', frequency: 'quarterly', category: 'aeration',  task: 'Diffuser pressure trend review per zone', responsibility: 'Design lead', notes: 'Track fouling rate' },
  { id: 't9', frequency: 'quarterly', category: 'blower',    task: 'Blower oil sample analysis', responsibility: 'Maintenance', notes: '' },
  { id: 't10', frequency: 'quarterly', category: 'compliance', task: 'Risk register review with Atkins / SEW', responsibility: 'Project manager', notes: '' },
  { id: 't11', frequency: 'annual',   category: 'aeration',  task: 'ROV / diver inspection of diffuser membranes', responsibility: 'Specialist contractor', notes: 'Photo + video record' },
  { id: 't12', frequency: 'annual',   category: 'aeration',  task: 'Sediment core re-sampling and SOD analysis', responsibility: 'Lab', notes: '' },
  { id: 't13', frequency: 'annual',   category: 'blower',    task: 'Blower major service (bearings, seals, belts)', responsibility: 'Manufacturer / agent', notes: '' },
  { id: 't14', frequency: 'annual',   category: 'sensors',   task: 'Sensor full calibration suite', responsibility: 'ICA tech', notes: '' },
  { id: 't15', frequency: 'annual',   category: 'compliance', task: 'BS 4142 noise re-survey at receptors', responsibility: 'Acoustic consultant', notes: 'If conditions changed' },
  { id: 't16', frequency: 'annual',   category: 'compliance', task: 'Reservoirs Act SE / IE inspection', responsibility: 'Panel engineer', notes: '' },
  { id: 't17', frequency: '5-yearly', category: 'aeration',  task: 'Diffuser membrane recovery-cleaning campaign', responsibility: 'Specialist contractor', notes: 'See fouling-drift page' },
  { id: 't18', frequency: '5-yearly', category: 'blower',    task: 'Blower mid-life overhaul', responsibility: 'Manufacturer / agent', notes: '' },
  { id: 't19', frequency: 'event-driven', category: 'aeration', task: 'Response to DO alarm > 30 min below threshold', responsibility: 'On-call engineer', notes: 'SLA: 4-hour response' },
  { id: 't20', frequency: 'event-driven', category: 'compliance', task: 'DWI / EA notification of any non-compliance', responsibility: 'Project manager', notes: 'Statutory timeline' },
];

const DEFAULT_SPARES: Spare[] = [
  { id: 's1', item: 'EPDM diffuser membranes (Ø300)', qtyOnSite: 4, qtyOnOrder: 0, leadTimeWeeks: 6, supplier: '', partNumber: '' },
  { id: 's2', item: 'Diffuser ballast assemblies', qtyOnSite: 2, qtyOnOrder: 0, leadTimeWeeks: 4, supplier: '', partNumber: '' },
  { id: 's3', item: 'Air-main quick-connect fittings', qtyOnSite: 6, qtyOnOrder: 0, leadTimeWeeks: 2, supplier: '', partNumber: '' },
  { id: 's4', item: 'Blower inlet filter elements', qtyOnSite: 4, qtyOnOrder: 0, leadTimeWeeks: 1, supplier: '', partNumber: '' },
  { id: 's5', item: 'Blower oil (specified grade)', qtyOnSite: 40, qtyOnOrder: 0, leadTimeWeeks: 1, supplier: '', partNumber: '' },
  { id: 's6', item: 'DO sensor membrane cartridges', qtyOnSite: 4, qtyOnOrder: 0, leadTimeWeeks: 3, supplier: '', partNumber: '' },
  { id: 's7', item: 'PLC spare CPU + I/O module', qtyOnSite: 1, qtyOnOrder: 0, leadTimeWeeks: 8, supplier: '', partNumber: '' },
];

const DEFAULT_STATE: OMState = {
  tasks: DEFAULT_TASKS,
  spares: DEFAULT_SPARES,
  contractStart: '',
  reviewInterval: 6,
};

const FREQ_LABEL: Record<Task['frequency'], string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  '5-yearly': '5-yearly',
  'event-driven': 'Event-driven',
};

export default function OMSchedulePage() {
  const navigate = useNavigate();
  const [s, setS] = useState<OMState>(() => loadPersisted<OMState>(KEY, DEFAULT_STATE));
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => { savePersisted(KEY, s); }, [s]);

  const filtered = filter === 'all' ? s.tasks : s.tasks.filter(t => t.frequency === filter);

  const updateTask = (id: string, patch: Partial<Task>) =>
    setS({ ...s, tasks: s.tasks.map(t => t.id === id ? { ...t, ...patch } : t) });
  const addTask = () => setS({ ...s, tasks: [...s.tasks, { id: uid(), frequency: 'monthly', category: 'aeration', task: '', responsibility: '', notes: '' }] });
  const removeTask = (id: string) => setS({ ...s, tasks: s.tasks.filter(t => t.id !== id) });

  const updateSpare = (id: string, patch: Partial<Spare>) =>
    setS({ ...s, spares: s.spares.map(sp => sp.id === id ? { ...sp, ...patch } : sp) });
  const addSpare = () => setS({ ...s, spares: [...s.spares, { id: uid(), item: '', qtyOnSite: 0, qtyOnOrder: 0, leadTimeWeeks: 0, supplier: '', partNumber: '' }] });
  const removeSpare = (id: string) => setS({ ...s, spares: s.spares.filter(sp => sp.id !== id) });

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Wrench}
        title="O&M Schedule"
        subtitle="Year-by-year operations & maintenance calendar with task register and spares list"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => { savePersisted(KEY, s); toast.success('Saved'); }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        }
      />

      {/* Header */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Contract start</label>
            <input type="date" value={s.contractStart}
              onChange={e => setS({ ...s, contractStart: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Schedule review interval (months)</label>
            <input type="number" value={s.reviewInterval}
              onChange={e => setS({ ...s, reviewInterval: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Total task count</label>
            <div className="text-2xl font-bold text-slate-100">{s.tasks.length}</div>
          </div>
        </div>
      </div>

      {/* Task filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', ...Object.keys(FREQ_LABEL)].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              filter === f ? 'bg-rb-brand text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}>
            {f === 'all' ? 'All' : FREQ_LABEL[f as Task['frequency']]} ({f === 'all' ? s.tasks.length : s.tasks.filter(t => t.frequency === f).length})
          </button>
        ))}
      </div>

      {/* Tasks */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-100">Task register</div>
          <button onClick={addTask} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-slate-100">
            <Plus className="w-3.5 h-3.5" /> Add task
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Frequency</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Category</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Task</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Responsibility</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Notes</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-b border-slate-800">
                  <td className="px-2 py-1">
                    <select value={t.frequency}
                      onChange={e => updateTask(t.id, { frequency: e.target.value as Task['frequency'] })}
                      className="bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100">
                      {Object.entries(FREQ_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select value={t.category}
                      onChange={e => updateTask(t.id, { category: e.target.value as Task['category'] })}
                      className="bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100">
                      <option value="aeration">Aeration</option>
                      <option value="blower">Blower</option>
                      <option value="controls">Controls</option>
                      <option value="sensors">Sensors</option>
                      <option value="civils">Civils</option>
                      <option value="compliance">Compliance</option>
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input value={t.task} onChange={e => updateTask(t.id, { task: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={t.responsibility} onChange={e => updateTask(t.id, { responsibility: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={t.notes} onChange={e => updateTask(t.id, { notes: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <button onClick={() => removeTask(t.id)} className="text-slate-500 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Spares */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-100">Spares register</div>
          <button onClick={addSpare} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-slate-100">
            <Plus className="w-3.5 h-3.5" /> Add spare
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Item</th>
                <th className="px-2 py-1.5 text-right text-slate-300">On site</th>
                <th className="px-2 py-1.5 text-right text-slate-300">On order</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Lead time (wks)</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Supplier</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Part no.</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {s.spares.map(sp => (
                <tr key={sp.id} className="border-b border-slate-800">
                  <td className="px-2 py-1">
                    <input value={sp.item} onChange={e => updateSpare(sp.id, { item: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={sp.qtyOnSite}
                      onChange={e => updateSpare(sp.id, { qtyOnSite: Number(e.target.value) })}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100 text-right" />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={sp.qtyOnOrder}
                      onChange={e => updateSpare(sp.id, { qtyOnOrder: Number(e.target.value) })}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100 text-right" />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={sp.leadTimeWeeks}
                      onChange={e => updateSpare(sp.id, { leadTimeWeeks: Number(e.target.value) })}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100 text-right" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={sp.supplier} onChange={e => updateSpare(sp.id, { supplier: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={sp.partNumber} onChange={e => updateSpare(sp.id, { partNumber: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <button onClick={() => removeSpare(sp.id)} className="text-slate-500 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
